// ProofDeliveryForm — the quote-level "your printed sample" surface: HOW MANY
// printed proof samples the customer needs, and WHERE each one is mailed
// (Nexus feedback FB-0036).
//
// It appears only once the quote resolves to a PHYSICAL proof. Checking
// "a printed sample, mailed to you" used to open nothing at all: the sample
// count was a hardcoded 1 on the wire and no address for it existed anywhere in
// the stack, so the person who asked for the sample had no way to say where it
// should go.
//
// The addresses are the SAME repeatable list the order's own destinations use —
// ShipToForm under purpose='proof' — not a second address editor. That is what
// keeps the numbering, the remove control, the saved-address picker, the country
// list, the inline errors and the reveal/scroll contract identical between the
// two lists, and it is why the completeness rule cannot drift between them.
//
// The counts here balance against the SAMPLE COUNT, never the order quantity.
// Mixing those two totals is the failure that would make every order carrying a
// proof unsubmittable.
//
// Intent capture only: no price, no postage and no proof charge is shown or
// computed here. Nexus prices the proof's own print + shipping from the count
// this surface captures (ADR-010 §12).

import { useTranslation } from 'react-i18next';

import { PROOF_QUANTITY_FIELD_KEY, type ShipTo } from 'shared';

import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';

import { OrderSubsection } from './OrderSubsection.js';
import { ShipToForm } from './ShipToForm.js';

type ProofDeliveryFormProps = {
  // How many printed samples the customer wants. null while unanswered — never
  // 0, which would read as a deliberate "send none".
  proofQuantity: number | null;
  onProofQuantityChange: (next: number | null) => void;
  // Where those samples are mailed. Always at least one entry — the editor seeds
  // a blank one the moment a physical proof appears on the quote.
  proofShipTos: ShipTo[];
  onProofShipTosChange: (next: ShipTo[]) => void;
  // Field-keyed inline errors ('proof_quantity', 'proof_ship_to.*'), shared with
  // the near-button summary so the customer reads one message in two places.
  errors: Record<string, string>;
  disabled?: boolean;
};

export function ProofDeliveryForm({
  proofQuantity,
  onProofQuantityChange,
  proofShipTos,
  onProofShipTosChange,
  errors,
  disabled,
}: ProofDeliveryFormProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const quantityError = errors[PROOF_QUANTITY_FIELD_KEY];

  function setQuantity(raw: string): void {
    // Whole samples only — the same rule the destinations' unit counts follow, so
    // the two number boxes on this page behave identically. A non-digit keystroke
    // is refused; an empty or zero box reads as "not answered yet", which the
    // submit gate reports as a miss, never as zero.
    const parsed = parseWholeNumberInput(raw);
    if (parsed === undefined) return;
    onProofQuantityChange(parsed === null || parsed < 1 ? null : parsed);
  }

  return (
    // The same wrapper the delivery subsections use, so the block reads as one of
    // them. Its id is a scroll target only: nothing carries `aria-controls` for
    // it, because this block is revealed by the quote's proof intent rather than
    // by a card the customer clicked.
    <OrderSubsection id="order-service-proof" title={t('proofShipTo.title')}>
      <p className="help help--flush">{t('proofShipTo.intro')}</p>

      <div
        className={`form-row${quantityError ? ' form-row--error' : ''}`}
        style={{ marginTop: 'var(--space-3, 12px)' }}
      >
        <label htmlFor={PROOF_QUANTITY_FIELD_KEY}>
          {t('proofShipTo.quantity.label')}
          {/* Asterisk is the required signal — a shape, not colour alone —
              matching SchemaForm's required-marker pattern. */}
          <span className="required-marker"> *</span>
        </label>
        <span className="help" style={{ display: 'block' }}>
          {t('proofShipTo.quantity.hint')}
        </span>
        <input
          id={PROOF_QUANTITY_FIELD_KEY}
          {...WHOLE_NUMBER_INPUT_PROPS}
          required
          value={proofQuantity == null ? '' : String(proofQuantity)}
          onChange={(e) => setQuantity(e.target.value)}
          disabled={disabled}
          aria-invalid={quantityError ? true : undefined}
          aria-describedby={quantityError ? `${PROOF_QUANTITY_FIELD_KEY}-error` : undefined}
        />
        {quantityError && (
          <span className="field-error" id={`${PROOF_QUANTITY_FIELD_KEY}-error`}>
            {quantityError}
          </span>
        )}
      </div>

      {/* The SAME address list the order's destinations use, under its own key
          namespace. `expectedTotal` is the sample count — what these counts are
          a split of — and never the order quantity. */}
      <ShipToForm
        purpose="proof"
        shipTos={proofShipTos}
        onChange={onProofShipTosChange}
        expectedTotal={proofQuantity}
        errors={errors}
        disabled={disabled}
      />
    </OrderSubsection>
  );
}
